"use client";

import type { Settings } from "@types";
import Image from "next/image";
import { ScrollParallax } from "react-just-parallax";
import Button from "@/components/inputs/Button";
import MailchimpForm from "@/components/MailchimpForm";
import { buttons, spacing, typography } from "@/styles/design-tokens";
import { BlockContent } from "./inputs/PortableTextComponents";

type FooterType = NonNullable<Settings["footer"]>;
type MailchimpType = NonNullable<Settings["mailchimp"]>;

// Explicit prop types for footer links to avoid implicit any
interface FooterLink {
  _key: string;
  text?: string;
  url?: string;
}

interface FooterProps {
  footer: FooterType;
  mailchimp: MailchimpType;
}

export default function Footer({ footer, mailchimp }: FooterProps) {
  const year = new Date().getFullYear();

  return (
    <>
      <section className={spacing.section}>
        {mailchimp?.mailchimpKey && (
          <div className={spacing.container}>
            {(mailchimp?.text || mailchimp?.copy) && (
              <div className="text-left max-w-5xl mx-auto sm:text-center">
                {mailchimp.text && (
                  <p className={typography.h6}>{mailchimp.text}</p>
                )}
                {mailchimp.copy && <BlockContent value={mailchimp.copy} />}
              </div>
            )}
              <MailchimpForm url={mailchimp.mailchimpKey} />
          </div>
          )}
        <div className="absolute left-10 top-80 rotate-180 hidden sm:block">
          <ScrollParallax>
            <Image
              src="/images/chopsticks.gif"
              alt=""
              width={240}
              height={120}
            />
          </ScrollParallax>
        </div>
        <div className="absolute left-12 top-2/3 hidden sm:block">
          <ScrollParallax>
            <Image src="/images/sushi.gif" alt="" width={150} height={120} />
          </ScrollParallax>
        </div>
        <div className="absolute right-12 bottom-1/3 hidden sm:block">
          <ScrollParallax>
            <Image src="/images/ticket.gif" alt="" width={135} height={120} />
          </ScrollParallax>
        </div>
        <div className="absolute right-12 bottom-0 hidden sm:block">
          <ScrollParallax>
            <Image src="/images/stage.gif" alt="" width={135} height={120} />
          </ScrollParallax>
        </div>
      </section>

      <section>
        <div className="relative">
          <Image
            src="/images/footer-now.webp"
            alt="illustrated people partying"
            width={2450}
            height={1837}
            className="relative z-10 mx-auto"
          />
        </div>
      </section>

      <footer className="w-full py-3 border-y-2">
        <div className="w-full px-4 flex flex-col-reverse items-center justify-center gap-4 md:flex-row sm:justify-between sm:px-6">
          <p className={typography.body}>
            &copy; {year} {footer.copyrightText}
          </p>

          {footer.footerLinks && footer.footerLinks.length > 0 && (
            <nav
              aria-label="Footer navigation"
              className="flex flex-wrap items-center justify-center gap-3 md:justify-end"
            >
              {footer.footerLinks.map((link: FooterLink) => (
                <Button key={link._key} {...link} classes={buttons.link} />
              ))}
            </nav>
          )}
        </div>
      </footer>
    </>
  );
}
